import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { keyframes } from '@emotion/css';

import { Search } from '#components/common/Search';
import { BocadilloGavi } from '#components/mobile/importar/BocadilloGavi';
import { centimosDe, teclear } from '#components/mobile/inicio/hoyPuedes';
import { comprobarPlan } from '#components/mobile/plan/cerrojoPlan';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { Pildora } from '#components/mobile/ui/Pildora';
import { TecladoImporte } from '#components/mobile/ui/TecladoImporte';
import {
  color,
  densidad,
  num,
  radio,
  texto,
} from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { useLocalPref } from '#hooks/useLocalPref';
import { useReducedMotion } from '#hooks/useReducedMotion';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import { useUndo } from '#hooks/useUndo';
import { envelopeBudget } from '#spreadsheet/bindings';

import { categoriasCandidatas, LISTO_PARA_ASIGNAR } from './cubrir';
import { repartirOrigenesPorCuenta } from './cuentaCategoria';
import { Montones, MS_MONEDAS } from './MontonesMover';
import {
  anotarUsada,
  destinosMover,
  importeSugerido,
  monedasQueCaen,
  movimientoDe,
  origenesMover,
  seccionesMover,
} from './mover';
import type { CandidataMover, MotivoProbable } from './mover';
import {
  conLinea,
  InterruptorMostrarTodas,
  useLineaTraspaso,
  useMostrarTodas,
} from './PorCuenta';
import { useCuentasCategorias } from './useCuentasCategorias';
import { useDatosObjetivos } from './useDatosObjetivos';

/** Saldo positivo legible en claro y en oscuro (igual que en «Cubrir»). */
const TINTA_SALDO = `color-mix(in oklab, ${color.ok} 72%, ${color.fg})`;
/** Latón del borde de la carta y del destello (estilo Rumbo). */
const LATON = '#c9973b';
const LATON_CLARO = '#f2d38a';

const destello = keyframes({
  from: {
    backgroundColor: `color-mix(in oklab, ${LATON_CLARO} 70%, transparent)`,
  },
  to: { backgroundColor: 'transparent' },
});
const entra = keyframes({
  from: { transform: 'translateX(40%)', opacity: 0 },
  to: { transform: 'none', opacity: 1 },
});
const vuelve = keyframes({
  from: { transform: 'translateX(-30%)', opacity: 0 },
  to: { transform: 'none', opacity: 1 },
});

type Lado = 'desde' | 'hacia';

type HojaMoverProps = {
  month: string;
  onBudgetAction: (month: string, type: string, args: unknown) => unknown;
  /**
   * Categoría ya elegida al abrir (desde la ficha o el teclado). Sin ella,
   * los dos extremos empiezan vacíos.
   */
  origen?: string | null;
  /**
   * `hacia`: `origen` va en «Hacia» («Traer dinero de otra categoría»). Por
   * defecto, en «Desde».
   */
  modoInicial?: Lado;
  onCerrar: () => void;
};

/**
 * «Mover dinero» (Vadik, 24-oct-2026): Gavi saluda y solo hay «Desde» y
 * «Hacia». Tocar uno abre la lista de categorías; al elegir se vuelve a la
 * ruta. Con los dos elegidos aparece la cifra (ya propuesta) y el botón
 * «Mover» se pone verde; al pulsarlo el barco navega de un puerto al otro
 * (`transfer-category`, o `transfer-available` si sale de «Listo para
 * asignar») con aviso y deshacer, y la hoja se cierra.
 */
export function HojaMover(props: HojaMoverProps) {
  return (
    <SheetNameProvider name={monthUtils.sheetForMonth(props.month)}>
      <HojaMoverContenido {...props} />
    </SheetNameProvider>
  );
}

function HojaMoverContenido({
  month,
  onBudgetAction,
  origen,
  modoInicial = 'desde',
  onCerrar,
}: HojaMoverProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const { showUndoNotification } = useUndo();
  const quieta = useReducedMotion();

  const {
    data: { list: categorias, grouped: grupos } = {
      list: [],
      grouped: [],
    },
  } = useCategories();
  const candidatas = useMemo(() => categoriasCandidatas(grupos), [grupos]);
  const datos = useDatosObjetivos(month, candidatas);
  const toBudget = useSheetValue<'envelope-budget', 'to-budget'>(
    envelopeBudget.toBudget,
  );

  const [extremos, setExtremos] = useState<Record<Lado, string | null>>({
    desde: modoInicial === 'desde' ? (origen ?? null) : null,
    hacia: modoInicial === 'hacia' ? (origen ?? null) : null,
  });
  // `null`: la ruta; si no, la lista para elegir ese extremo.
  const [eligiendo, setEligiendo] = useState<Lado | null>(null);
  // Lo que se busca en la lista (con el teclado del móvil: es texto).
  const [busqueda, setBusqueda] = useState('');
  const abrirLista = (lado: Lado) => {
    setBusqueda('');
    setEligiendo(lado);
  };
  // «Sueles mover aquí»: lo que más se mueve en este móvil.
  const [usadas, setUsadas] = useLocalPref('mover.usadas');
  const [recienElegido, setRecienElegido] = useState<Lado | null>(null);
  // `null` mientras no se toque: sigue a la cifra propuesta.
  const [importeElegido, setImporteElegido] = useState<number | null>(null);
  // Lo que se teclea con el teclado de Rumbo (nunca el del móvil); `null`
  // mientras no se escribe.
  const [escribiendo, setEscribiendo] = useState<string | null>(null);
  // Al soltar, los montones se quedan como estaban: el Disponible ya cambia
  // mientras caen las monedas.
  const [alSoltar, setAlSoltar] = useState<{
    arriba: number | null | undefined;
    abajo: number | null | undefined;
    caen: number;
  } | null>(null);
  const navegando = alSoltar != null;

  const saldoDe = (id: string | null) => {
    if (id == null) {
      return null;
    }
    return id === LISTO_PARA_ASIGNAR ? toBudget : datos.get(id)?.balance;
  };
  const nombreDe = (id: string) =>
    id === LISTO_PARA_ASIGNAR
      ? t('To Budget')
      : (categorias.find(c => c.id === id)?.name ?? '');

  const { desde, hacia } = extremos;
  const listos = desde != null && hacia != null;
  const saldoOrigen = saldoDe(desde);
  const saldoDestino = saldoDe(hacia);
  const sugerido = importeSugerido(saldoOrigen, saldoDestino);
  // Sin cifra que proponer (p. ej. sale de una categoría a 0), el teclado
  // sale ya abierto.
  const tecladoAbierto =
    listos && (escribiendo != null || (importeElegido == null && !sugerido));
  const importe =
    escribiendo != null
      ? centimosDe(escribiendo)
      : (importeElegido ?? sugerido);
  const falta =
    listos && saldoDestino != null && saldoDestino < 0 ? -saldoDestino : null;
  const todo = Math.max(0, saldoOrigen ?? 0);

  // Lista del extremo que se elige: sin el otro extremo. «Desde», primero
  // las que tienen dinero; «Hacia», las rojas primero. Todas se pueden elegir.
  const otro = eligiendo === 'desde' ? hacia : desde;
  const lista = useMemo(() => {
    if (eligiendo === 'desde') {
      return origenesMover({
        grupos,
        saldos: datos,
        toBudget,
        destino: hacia ?? '',
      });
    }
    if (eligiendo === 'hacia') {
      return destinosMover({
        grupos,
        saldos: datos,
        toBudget,
        origen: desde ?? '',
      });
    }
    return [];
  }, [datos, desde, eligiendo, grupos, hacia, toBudget]);
  // Por cuenta (como «Cubrir»): si el otro extremo es una categoría con
  // cuenta clara, por defecto solo se ofrecen las de su cuenta, las «sin
  // cuenta clara» y «Listo para asignar»; el resto, con el interruptor.
  const cuentasCat = useCuentasCategorias(month);
  const cuentaFija =
    otro != null &&
    otro !== LISTO_PARA_ASIGNAR &&
    cuentasCat.listo &&
    cuentasCat.cuentas.length > 1
      ? cuentasCat.cuentaDe(otro)
      : null;
  const reparto = useMemo(
    () =>
      repartirOrigenesPorCuenta({
        origenes: lista,
        cuentaGasto: cuentaFija,
        cuentaDe: cuentasCat.cuentaDe,
        ordenCuentas: cuentasCat.cuentas.map(c => c.id),
      }),
    [cuentaFija, cuentasCat, lista],
  );
  const [mostrarTodas, setMostrarTodas] = useMostrarTodas();
  // Buscando se busca en todas (también las de otras cuentas).
  const buscando = busqueda.trim() !== '';
  const secciones = useMemo(
    () =>
      seccionesMover({
        candidatas: buscando ? lista : reparto.visibles,
        grupos,
        lado: eligiendo ?? 'hacia',
        usadas,
        busqueda,
        nombreListo: t('To Budget'),
      }),
    [buscando, busqueda, eligiendo, grupos, lista, reparto, t, usadas],
  );
  const lineaTraspaso = useLineaTraspaso(cuentasCat);
  const nombreCuenta = (id: string) =>
    cuentasCat.cuentas.find(c => c.id === id)?.name ?? '';

  const elegir = (id: string) => {
    if (eligiendo == null) {
      return;
    }
    setExtremos(e => ({ ...e, [eligiendo]: id }));
    // Otra pareja: vuelve a proponerse la cifra.
    setImporteElegido(null);
    setEscribiendo(null);
    setRecienElegido(eligiendo);
    setEligiendo(null);
  };

  const elegirImporte = (valor: number) => {
    setEscribiendo(null);
    setImporteElegido(valor);
  };

  const mov =
    listos && !navegando
      ? movimientoDe({
          modo: 'desde',
          fijo: desde,
          elegido: hacia,
          importe,
          toBudget,
        })
      : null;

  const mover = () => {
    if (!mov || !comprobarPlan()) {
      return;
    }
    if (mov.desde === LISTO_PARA_ASIGNAR) {
      void onBudgetAction(month, 'transfer-available', {
        amount: mov.importe,
        month,
        category: mov.hacia,
      });
    } else {
      void onBudgetAction(month, 'transfer-category', {
        amount: mov.importe,
        from: mov.desde,
        to: mov.hacia,
        currencyCode: format.currency.code,
      });
    }
    setUsadas(anotarUsada(usadas, mov));
    const queda = (saldoOrigen ?? 0) - mov.importe;
    const movido = t('Moved {{amount}} to {{categoryName}}', {
      amount: format(mov.importe, 'financial'),
      categoryName: nombreDe(mov.hacia),
    });
    const cuentaDe = (id: string) =>
      id === LISTO_PARA_ASIGNAR ? null : cuentasCat.cuentaDe(id);
    showUndoNotification({
      message: conLinea(
        mov.desde !== LISTO_PARA_ASIGNAR && queda < 0
          ? `${movido} · ${t('{{categoryName}} is left at {{amount}}', {
              categoryName: nombreDe(mov.desde),
              amount: format(queda, 'financial'),
            })}`
          : movido,
        // Solo con «Mostrar todas»: entre categorías de cuentas distintas.
        lineaTraspaso(cuentaDe(mov.desde), cuentaDe(mov.hacia)),
      ),
    });
    if (quieta) {
      onCerrar();
      return;
    }
    // Las monedas caen del montón de arriba al de abajo y Gavi aplaude;
    // luego se cierra.
    setAlSoltar({
      arriba: saldoOrigen,
      abajo: saldoDestino,
      caen: monedasQueCaen(mov.importe),
    });
    window.setTimeout(onCerrar, MS_MONEDAS + 200);
  };

  const filaCandidata = (
    c: CandidataMover & { motivo?: MotivoProbable },
    prefijo = 'mover-a',
  ) => (
    <Button
      key={`${prefijo}-${c.id}`}
      variant="bare"
      data-testid={`${prefijo}-${c.id}`}
      onPress={() => elegir(c.id)}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        minHeight: 50,
        padding: '0 4px',
        borderRadius: 0,
        boxShadow: `inset 0 -1px 0 ${color.line}`,
        backgroundColor: 'transparent',
        color: color.fg,
        flexShrink: 0,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Text
          style={{
            ...densidad.nombre,
            fontWeight: c.id === LISTO_PARA_ASIGNAR ? 700 : 500,
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {nombreDe(c.id)}
        </Text>
        {c.enRojo && c.id !== LISTO_PARA_ASIGNAR ? (
          <Text
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: color.bad,
              flexShrink: 0,
            }}
          >
            <Trans>in the red</Trans>
          </Text>
        ) : (
          c.motivo === 'sueles' && (
            <Text
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: color.accent,
                flexShrink: 0,
              }}
            >
              {eligiendo === 'desde' ? (
                <Trans>you often take from here</Trans>
              ) : (
                <Trans>you often move here</Trans>
              )}
            </Text>
          )
        )}
      </View>
      <PrivacyFilter>
        <Pildora
          estado={c.saldo < 0 ? 'rojo' : c.saldo > 0 ? 'ok' : 'neutro'}
          style={c.saldo > 0 ? { color: TINTA_SALDO } : undefined}
        >
          {format(c.saldo, 'financial')}
        </Pildora>
      </PrivacyFilter>
    </Button>
  );

  if (eligiendo != null) {
    const nada =
      secciones.probables.length === 0 &&
      secciones.grupos.length === 0 &&
      secciones.listo == null;
    return (
      <Hoja
        aria-label={t('Move money')}
        data-testid="hoja-mover"
        alto="86%"
        onCerrar={onCerrar}
      >
        <View
          data-testid={`mover-lista-${eligiendo}`}
          style={{
            flex: 1,
            minHeight: 0,
            animation: quieta ? undefined : `${entra} 280ms ease-out`,
          }}
        >
          <Cabecera
            boton="atras"
            onPress={() => setEligiendo(null)}
            titulo={
              eligiendo === 'desde' ? (
                <Trans>Where does it come from?</Trans>
              ) : (
                <Trans>Where does it go?</Trans>
              )
            }
          />
          {cuentaFija && (
            <Text
              style={{
                ...texto.secundario,
                color: color.fg2,
                margin: '-4px 2px 8px',
              }}
            >
              {t('Money from {{accountName}}', {
                accountName: nombreCuenta(cuentaFija),
              })}
            </Text>
          )}
          <Search
            placeholder={t('Search category')}
            value={busqueda}
            onChange={setBusqueda}
            isInModal
            width="100%"
            height={46}
            style={{
              flexShrink: 0,
              marginBottom: 4,
              borderRadius: radio.boton,
              backgroundColor: color.surface,
              borderColor: color.line2,
            }}
          />
          <View
            data-testid="mover-lista-scroll"
            style={{
              // En bloque: las secciones no se encogen (se solaparían) y
              // los títulos se quedan arriba al bajar.
              display: 'block',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
            }}
          >
            {secciones.probables.length > 0 && (
              <View data-testid="mover-probables">
                <TituloSeccion destacado>
                  ★ <Trans>Most likely</Trans>
                </TituloSeccion>
                {secciones.probables.map(c =>
                  filaCandidata(
                    c,
                    c.id === LISTO_PARA_ASIGNAR ? 'mover-a' : 'mover-probable',
                  ),
                )}
              </View>
            )}
            {secciones.grupos.map(g => (
              <View key={g.id} data-testid={`mover-grupo-${g.id}`}>
                <TituloSeccion cuenta={g.filas.length}>
                  {g.nombre}
                </TituloSeccion>
                {g.filas.map(c => filaCandidata(c))}
              </View>
            ))}
            {secciones.listo && filaCandidata(secciones.listo)}
            {!buscando &&
              mostrarTodas &&
              reparto.otras.map(({ cuenta, origenes }) => (
                <View key={cuenta} data-testid={`mover-cuenta-${cuenta}`}>
                  <TituloSeccion cuenta={origenes.length}>
                    {nombreCuenta(cuenta)}
                  </TituloSeccion>
                  {origenes.map(c => filaCandidata(c))}
                </View>
              ))}
            {!buscando && cuentaFija && (
              <InterruptorMostrarTodas
                id="mover-mostrar-todas"
                isOn={mostrarTodas}
                onToggle={setMostrarTodas}
              />
            )}
            {nada && (
              <Text
                style={{
                  ...texto.cuerpo,
                  color: color.fg2,
                  padding: '16px 4px',
                }}
              >
                {buscando ? (
                  <Trans>No category is called that.</Trans>
                ) : (
                  <Trans>There is no category with money to move.</Trans>
                )}
              </Text>
            )}
          </View>
        </View>
      </Hoja>
    );
  }

  return (
    <Hoja
      aria-label={t('Move money')}
      data-testid="hoja-mover"
      onCerrar={onCerrar}
    >
      <View
        style={{
          animation:
            quieta || recienElegido == null
              ? undefined
              : `${vuelve} 280ms ease-out`,
        }}
      >
        <Cabecera
          boton="cerrar"
          onPress={onCerrar}
          titulo={<Trans>Move money</Trans>}
        />
        <View style={{ marginBottom: 14 }}>
          <BocadilloGavi estado={navegando ? 'aplaude' : undefined}>
            <Trans>
              It is normal to drift a little off course. Let's fine-tune it.
            </Trans>
          </BocadilloGavi>
        </View>

        {/* La ruta: el montón de monedas de «Desde» arriba y el de «Hacia» abajo. */}
        <View
          style={{
            position: 'relative',
            borderRadius: 22,
            backgroundColor: color.surface2,
            boxShadow: `inset 0 0 0 1.5px color-mix(in oklab, ${LATON} 60%, transparent)`,
            padding: '6px 12px 6px 62px',
          }}
        >
          <Montones
            saldoArriba={alSoltar ? alSoltar.arriba : saldoOrigen}
            saldoAbajo={alSoltar ? alSoltar.abajo : saldoDestino}
            caen={
              alSoltar ? alSoltar.caen : mov ? monedasQueCaen(mov.importe) : 0
            }
            soltando={navegando}
          />
          <Extremo
            lado="desde"
            id={desde}
            nombre={desde != null ? nombreDe(desde) : ''}
            saldo={alSoltar ? alSoltar.arriba : saldoOrigen}
            destello={recienElegido === 'desde' && !quieta}
            onPress={() => abrirLista('desde')}
          />
          <Extremo
            lado="hacia"
            id={hacia}
            nombre={hacia != null ? nombreDe(hacia) : ''}
            saldo={alSoltar ? alSoltar.abajo : saldoDestino}
            destello={recienElegido === 'hacia' && !quieta}
            onPress={() => abrirLista('hacia')}
          />
        </View>

        {/* La cifra: solo cuando ya hay desde y hacia, y viene propuesta. */}
        {listos && (
          <View
            data-testid="mover-cuanto"
            style={{ alignItems: 'center', marginTop: 14 }}
          >
            <Button
              variant="bare"
              data-testid="mover-importe"
              aria-label={t('Change amount')}
              onPress={() =>
                setEscribiendo(e => (e == null && !tecladoAbierto ? '' : null))
              }
              style={{
                padding: '2px 14px',
                borderRadius: 14,
                backgroundColor: 'transparent',
                color: color.fg,
                boxShadow: tecladoAbierto
                  ? `inset 0 0 0 2px ${color.accent}`
                  : undefined,
              }}
            >
              <PrivacyFilter>
                <Text style={{ ...num, ...CIFRA }}>
                  {escribiendo != null && escribiendo !== ''
                    ? `${escribiendo} ${format.currency.symbol}`
                    : format(importe, 'financial')}
                </Text>
              </PrivacyFilter>
            </Button>
            <Text
              style={{ ...densidad.pequeno, color: color.fg2, marginTop: 2 }}
            >
              {tecladoAbierto ? (
                <Trans>Type how much to move</Trans>
              ) : (
                <Trans>Tap the amount to change it</Trans>
              )}
            </Text>
            <View
              style={{
                flexDirection: 'row',
                gap: 6,
                justifyContent: 'center',
                marginTop: 8,
              }}
            >
              {falta != null && falta > 0 && (
                <Atajo
                  testId="mover-falta"
                  activo={escribiendo == null && importe === falta}
                  onPress={() => elegirImporte(falta)}
                >
                  <PrivacyFilter>
                    {t("What's missing · {{amount}}", {
                      amount: format(falta, 'financial'),
                    })}
                  </PrivacyFilter>
                </Atajo>
              )}
              {todo > 0 && (
                <Atajo
                  testId="mover-todo"
                  activo={escribiendo == null && importe === todo}
                  onPress={() => elegirImporte(todo)}
                >
                  <PrivacyFilter>
                    {t('All · {{amount}}', {
                      amount: format(todo, 'financial'),
                    })}
                  </PrivacyFilter>
                </Atajo>
              )}
            </View>
            {tecladoAbierto && (
              <View style={{ width: '100%', marginTop: 10 }}>
                <TecladoImporte
                  testId="mover"
                  onTecla={k => setEscribiendo(e => teclear(e ?? '', k))}
                />
              </View>
            )}
          </View>
        )}

        <Boton
          bloque
          data-testid="mover-boton"
          isDisabled={!mov}
          onPress={mover}
          style={{ marginTop: 14, gap: 10 }}
        >
          <Velero size={20} />
          {listos ? (
            <PrivacyFilter>
              {t('Move {{amount}}', {
                amount: format(mov?.importe ?? importe, 'financial'),
              })}
            </PrivacyFilter>
          ) : (
            <Trans>Choose from and to</Trans>
          )}
        </Boton>
      </View>
    </Hoja>
  );
}

const CIFRA = {
  fontSize: 40,
  fontWeight: 800,
  letterSpacing: '-0.035em',
  lineHeight: 1.05,
} as const;

/** Título de sección de la lista: se queda arriba al bajar. */
function TituloSeccion({
  children,
  cuenta,
  destacado = false,
}: {
  children: ReactNode;
  /** Cuántas hay en la sección (a la derecha). */
  cuenta?: number;
  destacado?: boolean;
}) {
  return (
    <View
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: '12px 4px 6px',
        backgroundColor: color.surfaceHoja,
      }}
    >
      <Text
        style={{
          ...texto.etiqueta,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 800,
          color: destacado ? color.accent : color.fg2,
        }}
      >
        {children}
      </Text>
      {cuenta != null && (
        <Text style={{ ...texto.etiqueta, ...num, color: color.fg3 }}>
          {cuenta}
        </Text>
      )}
    </View>
  );
}

function Cabecera({
  boton,
  onPress,
  titulo,
}: {
  boton: 'cerrar' | 'atras';
  onPress: () => void;
  titulo: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginBottom: 12,
      }}
    >
      <Button
        variant="bare"
        aria-label={boton === 'cerrar' ? t('Close') : t('Back')}
        data-testid={boton === 'cerrar' ? 'mover-cerrar' : 'mover-volver'}
        onPress={onPress}
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          backgroundColor: color.surface2,
          color: color.fg,
          flexShrink: 0,
        }}
      >
        <Icono nombre={boton === 'cerrar' ? 'x' : 'cl'} size={18} />
      </Button>
      <Text style={{ ...texto.titulo, fontSize: 19, color: color.fg, flex: 1 }}>
        {titulo}
      </Text>
    </View>
  );
}

function Extremo({
  lado,
  id,
  nombre,
  saldo,
  destello: conDestello,
  onPress,
}: {
  lado: Lado;
  id: string | null;
  nombre: string;
  saldo: number | null | undefined;
  destello: boolean;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  return (
    <Button
      variant="bare"
      data-testid={`mover-${lado}`}
      onPress={onPress}
      style={{
        display: 'block',
        textAlign: 'left',
        minHeight: 76,
        padding: '12px 4px',
        borderRadius: 14,
        backgroundColor: 'transparent',
        color: color.fg,
        borderTop: lado === 'hacia' ? `1px solid ${color.line}` : undefined,
        animation: conDestello ? `${destello} 700ms ease-out` : undefined,
      }}
    >
      <Text style={{ ...texto.etiqueta, color: color.fg3, display: 'block' }}>
        {lado === 'desde' ? <Trans>Move from</Trans> : <Trans>Move to</Trans>}
      </Text>
      {id == null ? (
        <Text
          style={{
            ...texto.fila,
            fontSize: 16,
            color: color.fg3,
            display: 'inline-block',
            marginTop: 6,
            border: `1.5px dashed ${color.line2}`,
            borderRadius: 12,
            padding: '6px 12px',
          }}
        >
          {lado === 'desde' ? (
            <Trans>Which category does it come from?</Trans>
          ) : (
            <Trans>Which category does it go to?</Trans>
          )}
        </Text>
      ) : (
        <>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 8,
              marginTop: 4,
            }}
          >
            <Text
              style={{
                fontSize: 18,
                fontWeight: 800,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {nombre}
            </Text>
            {saldo != null && (
              <PrivacyFilter>
                <Text
                  style={{
                    ...num,
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                    color: saldo < 0 ? color.bad : color.fg2,
                    fontWeight: saldo < 0 ? 700 : 500,
                  }}
                >
                  {saldo < 0
                    ? t('Short by {{amount}}', {
                        amount: format(-saldo, 'financial'),
                      })
                    : t('Has {{amount}}', {
                        amount: format(saldo, 'financial'),
                      })}
                </Text>
              </PrivacyFilter>
            )}
          </View>
          <Text
            style={{
              ...densidad.pequeno,
              color: color.accent,
              display: 'block',
              marginTop: 2,
            }}
          >
            <Trans>Change</Trans>
          </Text>
        </>
      )}
    </Button>
  );
}

/** Velero del botón «Mover», del color del texto. */
function Velero({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} aria-hidden>
      <path d="M20 5 L20 27" stroke="currentColor" strokeWidth="2.4" />
      <path d="M21 7 L32 25 L21 25 Z" fill="currentColor" />
      <path d="M19 10 L11 25 L19 25 Z" fill="currentColor" opacity={0.7} />
      <path d="M6 28 L34 28 L29 35 L11 35 Z" fill="currentColor" />
    </svg>
  );
}

function Atajo({
  testId,
  activo,
  onPress,
  children,
}: {
  testId: string;
  activo: boolean;
  onPress: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      variant="bare"
      data-testid={testId}
      onPress={onPress}
      style={{
        ...densidad.pequeno,
        fontWeight: 800,
        minHeight: 30,
        padding: '0 10px',
        borderRadius: radio.pildora,
        backgroundColor: activo ? color.accentSoft : color.surface,
        color: activo ? color.accent : color.fg2,
        border: `1px solid ${activo ? color.accent : color.line}`,
      }}
    >
      {children}
    </Button>
  );
}
